import { useCallback, useState } from 'react';
import html2canvas from 'html2canvas';

const DEFAULT_BACKGROUND_COLOR = '#f0f0f0';
const DEFAULT_SCALE = 2;
const DEFAULT_FILE_NAME = 'trashie-impact.png';

const canvasToBlob = (canvas) => new Promise((resolve) => {
  canvas.toBlob(resolve, 'image/png');
});

export const shareElementAsImage = async ({
  element,
  shareButtonSelector,
  fileName = DEFAULT_FILE_NAME,
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  scale = DEFAULT_SCALE,
}) => {
  if (!element) {
    return false;
  }

  const shareButton = shareButtonSelector ? element.querySelector(shareButtonSelector) : null;

  try {
    if (shareButton) {
      shareButton.style.visibility = 'hidden';
    }

    const canvas = await html2canvas(element, {
      backgroundColor,
      scale,
    });
    const blob = await canvasToBlob(canvas);

    if (!blob) {
      return false;
    }

    const file = new File([blob], fileName, {
      type: 'image/png',
    });

    if (navigator.canShare?.({ files: [file] }) && navigator.share) {
      await navigator.share({ files: [file] });
      return true;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    return true;
  } catch {
    return false;
  } finally {
    if (shareButton) {
      shareButton.style.visibility = '';
    }
  }
};

const useShareCardAsImage = ({
  cardRef,
  shareButtonSelector,
  fileName = DEFAULT_FILE_NAME,
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  scale = DEFAULT_SCALE,
}) => {
  const [isSharing, setIsSharing] = useState(false);

  const share = useCallback(async () => {
    setIsSharing(true);
    try {
      return await shareElementAsImage({
        element: cardRef?.current,
        shareButtonSelector,
        fileName,
        backgroundColor,
        scale,
      });
    } finally {
      setIsSharing(false);
    }
  }, [backgroundColor, cardRef, fileName, scale, shareButtonSelector]);

  return {
    share,
    isSharing,
  };
};

export default useShareCardAsImage;
